import { errorMessage } from 'constants/CommonConstants';
import { formRequiredRules } from 'helper';
import useProveOtpNew from 'hooks/useProveOtpNew';
import React, { useEffect, useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { CommonNumberInput } from 'sharedComponents/inputGroup/CommonNumberInput';
import CustomOwlLoader from 'sharedComponents/loader/CustomOwlLoader';
import { RootState } from 'store/store';
import Logger from 'utils/logger';

const VerifyOTPComponent: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onNext, onBack }, ref) => {
  const { t } = useTranslation();
  const isMobile = true;
  const [resendDisabled, setResendDisabled] = React.useState(false);

  const { publicLeadCreationData } = useSelector((state: RootState) => state.creditScoreStore);

  const { authenticateClientData } = useSelector((state: RootState) => state.auth);

  const [timer, setTimer] = React.useState(30);
  const { control, trigger, setError, getValues } = useFormContext();

  const hasCalledNextStep = React.useRef(false);
  const hasCalledMounted = React.useRef(false);

  // Redux State̦
  const { verifyOtpData } = useSelector((state: RootState) => state.commonModuleStore);

  const { startProveVerification, otpActions, validationError, otpSent, ERROR_CODES } = useProveOtpNew();

  const phone = getValues('phone');

  useEffect(() => {
    if (Object.keys(verifyOtpData).length && !verifyOtpData?.message?.valid) {
      // Error in verifyOtp API, show manual error for otp field
      setError('otp', {
        type: 'manual',
        message:
          typeof verifyOtpData?.message === 'string' ? verifyOtpData?.message : t('stepperFormTradeIn.invalidOtpError'),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyOtpData]);

  const proveVerification = (phoneNumber: string) => {
    startProveVerification(isMobile, publicLeadCreationData?.data, phoneNumber, () => {
      Logger.custom('prove_verification_completed', {
        id: publicLeadCreationData?.data?.Lead?.leadId || '',
        client: authenticateClientData?.data?.client || '',
        widgetName: authenticateClientData?.data?.widgetName || '',
        module: 'Trade-In',
      });
      onNext?.(); // Call the next step function in the parent
    }).catch((error) => {
      hasCalledMounted.current = false;
      console.error('Error in startProveVerification:', error?.message);

      Logger.error('Error in startProveVerification:', {
        errorData: error?.message || error,
        client: authenticateClientData?.data?.client || '',
        widgetName: authenticateClientData?.data?.widgetName || '',
        module: 'Trade-In',
      });

      if (error?.message === ERROR_CODES.AUTH_TOKEN_MISSING) {
        onNext?.();
      } else {
        setError('otp', {
          type: 'manual',
          message: t('stepperForm.invalidOtp'),
        });
      }
    });

    // sendOTP(proveData?.data?.authToken, confirmMobile, () => {
    //   onNext?.(); // Call the next step function in the parent
    // }).catch((error) => {
    //   console.error('Error in proveVerification:', error?.message);
    //   hasCalledMounted.current = false;
    //   console.error('Error in startProveVerification:', error?.message);
    //   if (error?.message === ERROR_CODES.AUTH_TOKEN_MISSING) {
    //     onNext?.();
    //   } else {
    //     setError('otp', {
    //       type: 'manual',
    //       message: t('stepperForm.invalidOtp'),
    //     });
    //   }
    // });
  };

  useEffect(() => {
    if (hasCalledMounted.current) return;
    hasCalledMounted.current = true;

    proveVerification(phone);
  }, []);

  useEffect(() => {
    if (validationError) {
      console.error('Validation error:', validationError);
      hasCalledNextStep.current = false;

      Logger.error('Prove validation error:', {
        errorData: validationError,
        client: authenticateClientData?.data?.client || '',
        widgetName: authenticateClientData?.data?.widgetName || '',
        module: 'Trade-In',
      });
      proveVerification(phone);

      // resendOtp(confirmMobile, true);

      if (validationError == ERROR_CODES.VALIDATION_ERROR) {
        setError('otp', {
          type: 'manual',
          message: t('stepperForm.invalidOtp'),
        });
      }

      // Display the error message to the user as needed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationError]);

  const handleResendClick = async () => {
    proveVerification(phone);

    // Simulate sending a new OTP (you should replace this with your actual logic)

    // Disable the resend button
    setResendDisabled(true);

    // Start a timer for 30 seconds
    let countdown = 30;
    setTimer(countdown);

    const timerInterval = setInterval(() => {
      countdown -= 1;
      setTimer(countdown);

      // Enable the resend button when the timer reaches 0
      if (countdown <= 0) {
        clearInterval(timerInterval);
        setResendDisabled(false);
      }
    }, 1000);
  };

  const nextStep = async () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    const { otp } = getValues();

    if (otpActions?.submitOtp) {
      Logger.custom('otp_submitted', {
        id: publicLeadCreationData?.data?.Lead?.leadId || '',
        client: authenticateClientData?.data?.client || '',
        widgetName: authenticateClientData?.data?.widgetName || '',
        module: 'Trade-In',
      });
      otpActions?.submitOtp(otp);
    }
  };

  const backStep = () => {
    onBack?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  return (
    <div className={`csc-screens__form-col shadow ${!otpSent ? 'position-static' : ''}`}>
      {otpSent ? (
        <>
          <div className='trade-in-stepper__otp-form__content text-center'>
            <h4 className='trade-in-stepper__otp-form__content__title'>{t('stepperFormTradeIn.mobileVerification')}</h4>
            <p>{t('stepperFormTradeIn.note6')}</p>
          </div>

          <>
            <div className='form-group'>
              <CommonNumberInput
                name='otp'
                control={control}
                label={t('stepperFormTradeIn.enterCode')}
                placeholder={t('stepperFormTradeIn.enterCode')}
                rules={{
                  ...formRequiredRules(errorMessage(t).codeRequired),
                }}
                thousandSeparator={false}
                triggerValidationForField={trigger}
              />
            </div>

            {resendDisabled && <p className='resend-otp pb-3'> {t('stepperFormTradeIn.resendOtp', { timer })}</p>}
            <div className='form-group'>
              <div className='resend-otp'>
                {t('stepperFormTradeIn.didntReceiveCode')}
                <button className='resend-otp__cta' type='button' onClick={handleResendClick} disabled={resendDisabled}>
                  {t('stepperFormTradeIn.resend')}
                </button>
              </div>
            </div>
          </>
        </>
      ) : (
        <CustomOwlLoader show={!otpSent} />
      )}
    </div>
  );
};

const VerifyOTPComponentStep = React.forwardRef(VerifyOTPComponent);

export default VerifyOTPComponentStep;
